import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, Shield, TrendingUp, Zap } from "lucide-react";
import { SIGN_IN_PATH, SIGN_UP_PATH } from "@/lib/auth-routes";
import syntraLogo from "@/assets/syntra-logo.png";
import syntraSymbol from "@/assets/syntra-symbol.svg";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={syntraSymbol} alt="Syntra" className="h-9 w-auto" />
            <span className="font-bold text-xl gradient-syntra-blue-text hidden sm:inline">SYNTRA</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to={SIGN_IN_PATH}>
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to={SIGN_UP_PATH}>
              <Button className="gradient-primary text-white">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="container text-center">
          <div className="flex justify-center mb-8">
            <img src={syntraLogo} alt="Syntra - The Execution Engine for Real Estate Investing" className="h-16 md:h-20 w-auto" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Scale your{" "}
            <span className="gradient-syntra-red-text">real estate portfolio</span>
            <br />
            with confidence
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            From deal sourcing to property management, Syntra is the all-in-one platform 
            that helps you execute your investment strategy with precision.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to={SIGN_UP_PATH}>
              <Button size="lg" className="gradient-primary text-white">
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-secondary text-secondary hover:bg-secondary/10">
              Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            Everything you need to succeed
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Purpose-built for real estate investors who want to scale systematically
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: TrendingUp,
                title: "Deal Pipeline",
                description: "Track every opportunity from lead to close with our visual pipeline.",
                color: "text-primary",
                bg: "bg-primary/10",
              },
              {
                icon: Building2,
                title: "Portfolio Management",
                description: "Manage properties, units, and tenants all in one place.",
                color: "text-secondary",
                bg: "bg-secondary/10",
              },
              {
                icon: Zap,
                title: "Rehab Tracking",
                description: "Budget, schedule, and track renovation projects with precision.",
                color: "text-accent",
                bg: "bg-accent/10",
              },
              {
                icon: Shield,
                title: "Secure & Multi-tenant",
                description: "Your data is isolated and protected with enterprise-grade security.",
                color: "text-success",
                bg: "bg-success/10",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="p-6 rounded-xl bg-card border border-border shadow-soft hover:shadow-elevated transition-shadow"
              >
                <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center mb-4", feature.bg)}>
                  <feature.icon className={cn("w-6 h-6", feature.color)} />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container">
          <div className="relative overflow-hidden rounded-2xl gradient-syntra-blue p-12 text-center">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4 text-white">Ready to scale your portfolio?</h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Join thousands of investors using Syntra to manage their real estate business.
              </p>
              <Link to={SIGN_UP_PATH}>
                <Button size="lg" className="bg-white text-secondary hover:bg-white/90">
                  Get Started for Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <img src={syntraSymbol} alt="Syntra" className="h-6 w-auto" />
            <span>© 2024 Syntra. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
            <a href="#" className="hover:text-foreground transition-colors">Terms</a>
            <a href="#" className="hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function cn(...classes: (string | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}
